import React, { useState } from "react";

import { Button, Modal } from "../../components/ui";
import { RequestAmountCard } from "./RequestAmountCard";
import { useRequestLookbook } from "./useRequestLookbook";

/**
 * Step one of PROCEED: what each request is worth.
 *
 * The cards are in the order the tray set, because that is the order they will
 * appear in the PDF — a rep filling prices in should be reading down the same
 * sequence they just arranged.
 *
 * Every field auto-saves on blur, straight onto the REQUEST (not the lookbook):
 * two lookbooks containing the same request must agree about what we will pay
 * for it. There is no Save button here for that reason.
 */
export const RequestAmountsModal = () => {
  const {
    amountsOpen,
    closeStep,
    openCover,
    order,
    selected,
    count,
    saveAmounts,
    saveState,
    applyPrefills,
  } = useRequestLookbook();
  const [applying, setApplying] = useState(false);

  // Suggestions shown in empty fields are only saved here, so the PDF prints
  // what the rep saw, and closing the dialog without proceeding writes nothing.
  const proceed = async () => {
    setApplying(true);
    try {
      await applyPrefills();
    } finally {
      setApplying(false);
    }
    openCover();
  };

  const footer = (
    <>
      <span className="rlb-modal__footer-hint">
        Saved as you type. Leave a field blank to keep it off the PDF.
      </span>
      <Button size="lg" onClick={proceed} loading={applying}>
        Proceed
      </Button>
    </>
  );

  return (
    <Modal
      open={amountsOpen}
      onClose={closeStep}
      variant="dialog"
      size="lg"
      title="Request Amounts"
      description={`${count} ${count === 1 ? "request" : "requests"} in this lookbook`}
      footer={footer}
      className="rlb-modal rlb-modal--amounts"
    >
      <ul className="rlb-amount-list">
        {order.map((id, index) => {
          const request = selected[id];
          // An id can outlive its object while the selection settles — skip
          // rather than dereference `request.product` and take the list down.
          if (!request) return null;

          return (
            <RequestAmountCard
              key={id}
              request={request}
              position={index + 1}
              saveState={saveState[id]}
              onSave={(patch) => saveAmounts(id, patch)}
            />
          );
        })}
      </ul>
    </Modal>
  );
};

export default RequestAmountsModal;
